// WelcomeOverlay — iter179.
// Premium hospitality-grade fresh-login overlay.
//
// • Fetches /api/tenant/welcome on mount (greeting, quote, brand, landing_path).
// • Auto-dismisses after 30s OR on user action (skip / workspace card click).
// • Records dismissal in sessionStorage ('cx_welcome_seen_{tenant_id}_{date}')
//   so a hard refresh on the same day does NOT re-trigger it.
// • Configurable per-tenant via overlay_mode: 'every_login' | 'daily' | 'disabled'.
//
// UX invariants:
//   - Hospitality green palette, NOT a Windows-style modal.
//   - Sub-20% bg blur — content stays in spotlight.
//   - Quote category pill (warm amber) + monospaced day-of-year for trust.
//   - Quick workspace cards drive operational orientation.
//   - Esc key skips. Click outside also skips.
//   - 30s circular timer indicator (top-right).
//   - Time-aware greeting (morning/afternoon/evening) baked into payload.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { listEnabledLandings } from '../config/modules';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  X, ArrowRight, Sparkles, Quote as QuoteIcon,
  LayoutDashboard, BarChart3, Users, ClipboardList,
  Package, ChefHat, Wallet, MessageSquare, Megaphone,
  UserCog, Building2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTO_DISMISS_MS = 30000;
const ICONS = {
  '/admin/dashboard': LayoutDashboard,
  '/admin/pnl': BarChart3,
  '/admin/cost-insights': BarChart3,
  '/admin/insights': BarChart3,
  '/admin/customers': Users,
  '/admin/customer-experience': Sparkles,
  '/admin/cx': Sparkles,
  '/admin/reservations': ClipboardList,
  '/admin/checklist': ClipboardList,
  '/admin/inventory': Package,
  '/admin/recipes': ChefHat,
  '/admin/petty-cash': Wallet,
  '/admin/action-comm': MessageSquare,
  '/admin/marketing': Megaphone,
  '/admin/managers': UserCog,
  '/admin/outlets': Building2,
  '/manager/entry': ClipboardList,
};

function todayKey() {
  return new Date().toISOString().slice(0, 10);  // YYYY-MM-DD
}

function shouldShowOverlay(mode, tenantId) {
  if (mode === 'disabled') return false;
  if (!tenantId) return false;
  const key = `welcome_seen_${tenantId}_${todayKey()}`;
  if (mode === 'daily') {
    // Show once per day per tenant. localStorage persists across reload.
    return !localStorage.getItem(key);
  }
  // 'every_login' → show on every fresh session (no localStorage check).
  return true;
}

function markSeen(mode, tenantId) {
  if (!tenantId) return;
  if (mode === 'daily') {
    localStorage.setItem(`welcome_seen_${tenantId}_${todayKey()}`, '1');
  }
  // 'every_login' → nothing to persist; controlled by sessionStorage flag set
  // by AuthContext on successful login.
}

export default function WelcomeOverlay({ onComplete }) {
  const { user, tenantFeatures, getHeaders } = useAuth();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [progress, setProgress] = useState(100);
  const [closing, setClosing] = useState(false);

  // iter204 — Studio users are a dedicated consultant product. They must
  // never see the generic admin welcome interstitial (which exposes
  // shortcuts to modules they cannot access). Bypass immediately and
  // route them to the EI workspace.
  useEffect(() => {
    if (user?.role === 'studio_user') {
      onComplete?.('/admin/expansion-intelligence');
      navigate('/admin/expansion-intelligence', { replace: true });
    }
  }, [user?.role, onComplete, navigate]);

  // Fetch welcome payload on mount
  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/tenant/welcome`, { headers: getHeaders() })
      .then((r) => { if (!cancel) setPayload(r.data); })
      .catch(() => { if (!cancel) onComplete?.(null); });
    return () => { cancel = true; };
  }, [getHeaders, onComplete]);

  // Decide whether to render based on overlay_mode + dedupe key
  useEffect(() => {
    if (!payload) return;
    if (!shouldShowOverlay(payload.overlay_mode, payload.tenant?.tenant_id)) {
      onComplete?.(payload.landing_path);
    }
  }, [payload, onComplete]);

  // Auto-dismiss countdown
  useEffect(() => {
    if (!payload) return;
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / AUTO_DISMISS_MS) * 100);
      setProgress(pct);
      if (elapsed >= AUTO_DISMISS_MS) {
        clearInterval(tick);
        handleDismiss(null);
      }
    }, 100);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  // Esc to skip
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleDismiss(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const enabledModules = useMemo(
    () => listEnabledLandings(user?.role, tenantFeatures, user).slice(0, 4),
    [user?.role, tenantFeatures],
  );

  const handleDismiss = (overridePath) => {
    if (closing) return;
    setClosing(true);
    markSeen(payload?.overlay_mode, payload?.tenant?.tenant_id);
    const target = overridePath || payload?.landing_path;
    setTimeout(() => {
      onComplete?.(target);
      if (target) navigate(target, { replace: true });
    }, 250);  // small fade-out
  };

  if (!payload) return null;
  // Studio users never render this overlay — see effect above.
  if (user?.role === 'studio_user') return null;
  // If overlay_mode bars rendering, the effect above already called onComplete.
  if (!shouldShowOverlay(payload.overlay_mode, payload.tenant?.tenant_id)) return null;

  const { heading, subtext, quote, tenant } = payload;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6 transition-opacity duration-300
        ${closing ? 'opacity-0' : 'opacity-100'}`}
      data-testid="welcome-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) handleDismiss(null); }}
    >
      {/* Backdrop — hospitality green tint, subtle blur */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F18]/85 via-[#0E2C22]/80 to-[var(--color-primary)]/85 backdrop-blur-md" />

      {/* Card */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Top hospitality gradient accent */}
        <div className="h-1.5 bg-gradient-to-r from-[var(--color-primary)] via-[#2D6A4F] to-[var(--color-primary)]" />

        <div className="p-7 sm:p-9">
          {/* Header row: brand + skip + countdown */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              {tenant?.logo_url ? (
                <div className="w-11 h-11 rounded-lg border border-[#E2E8DF] bg-white p-1 shadow-sm">
                  <img src={tenant.logo_url} alt={tenant.brand_name} className="w-full h-full object-contain" />
                </div>
              ) : (
                <div className="w-11 h-11 rounded-lg bg-[color:var(--color-primary)] text-white flex items-center justify-center font-semibold text-sm">
                  {(tenant?.brand_name || 'R').slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-[9px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold">
                  Operations Portal
                </div>
                <div className="text-[14px] font-semibold text-[#1A2421] leading-tight">
                  {tenant?.brand_name || 'RestroPulse'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Circular progress ring (CSS only, no SVG dep) */}
              <div
                className="relative w-7 h-7 rounded-full"
                style={{
                  background: `conic-gradient(var(--color-primary) ${progress}%, #E2E8DF ${progress}%)`,
                }}
                title={`${Math.ceil((progress / 100) * 30)}s`}
              >
                <div className="absolute inset-1 rounded-full bg-white" />
              </div>
              <button
                onClick={() => handleDismiss(null)}
                className="w-8 h-8 rounded-full hover:bg-[#F3F5F2] flex items-center justify-center text-[#5C6B64] hover:text-[#1A2421] transition-colors"
                aria-label="Skip welcome"
                data-testid="welcome-overlay-skip"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>

          {/* Greeting */}
          <h1
            className="text-[26px] sm:text-[32px] font-semibold text-[#1A2421] tracking-tight leading-tight mb-2"
            data-testid="welcome-overlay-heading"
          >
            {heading}
          </h1>
          <p className="text-[14px] sm:text-[15px] text-[#5C6B64] leading-relaxed mb-6 max-w-xl">
            {subtext}
          </p>

          {/* Quote card */}
          {quote?.text && (
            <Card className="border-[#E5E7E1] bg-gradient-to-br from-[#FAFBF8] to-[#F3F5F2] mb-6" data-testid="welcome-overlay-quote">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[color:var(--color-primary)]/8 text-[color:var(--color-primary)] flex items-center justify-center shrink-0">
                    <QuoteIcon className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <blockquote className="text-[14.5px] sm:text-[15.5px] text-[#1A2421] leading-relaxed italic font-medium">
                      “{quote.text}”
                    </blockquote>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {quote.author && (
                        <span className="text-[12px] text-[#5C6B64]">— {quote.author}</span>
                      )}
                      {quote.category && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#D89B5B]/15 text-[#9C5F2D]">
                          {quote.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick workspace cards */}
          {enabledModules.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold mb-3">
                Jump straight in
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-7" data-testid="welcome-overlay-workspaces">
                {enabledModules.map((m) => {
                  const Icon = ICONS[m.path] || Sparkles;
                  return (
                    <button
                      key={m.path}
                      onClick={() => handleDismiss(m.path)}
                      className="text-left p-3 rounded-lg border border-[#E2E8DF] bg-white hover:border-[color:var(--color-primary)] hover:shadow-sm hover:-translate-y-0.5 transition-all group"
                      data-testid={`welcome-overlay-card-${m.feature || 'open'}`}
                    >
                      <div className="w-8 h-8 rounded-md bg-[#EEF4EF] text-[color:var(--color-primary)] flex items-center justify-center mb-2 group-hover:bg-[color:var(--color-primary)] group-hover:text-white transition-colors">
                        <Icon className="w-4 h-4" strokeWidth={1.75} />
                      </div>
                      <div className="text-[12px] font-semibold text-[#1A2421] truncate">{m.label}</div>
                      <div className="text-[10px] text-[#9AAA9F] mt-0.5">Open</div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Primary CTA */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-[11px] text-[#9AAA9F]">
              Auto-continuing in {Math.ceil((progress / 100) * 30)}s
            </div>
            <Button
              onClick={() => handleDismiss(null)}
              className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white h-9 text-[13px] px-4"
              data-testid="welcome-overlay-continue"
            >
              Continue to {payload.landing_path ? 'workspace' : 'portal'}
              <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>

          {/* iter180g — legal links inside the overlay for compliance + trust */}
          <div className="mt-4 pt-3 border-t border-[#E5E7E1] flex items-center justify-center gap-3 text-[10px] text-[#9AAA9F]">
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--color-primary)] transition-colors" data-testid="welcome-privacy-link">Privacy</a>
            <span className="text-[#D4D9D2]">·</span>
            <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--color-primary)] transition-colors" data-testid="welcome-terms-link">Terms</a>
          </div>
        </div>
      </div>
    </div>
  );
}
